import React, { useState, useMemo, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import {
  HiOutlineShieldCheck,
  HiOutlineBell,
  HiOutlineMagnifyingGlass,
  HiOutlineSun,
  HiOutlineMoon,
  HiOutlineArrowRightOnRectangle,
  HiOutlineUser,
  HiOutlineChevronDown,
  HiOutlineClock,
  HiOutlineCog,
  HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";
import CommandPalette from "./CommandPalette";
import MaXSidebarLogo from "./MaXSidebarLogo";
import NotificationDropdown from "./NotificationDropdown";
import SettingsLauncherDropdown from "./SettingsLauncherDropdown";
import AppLauncherDropdown from "./AppLauncherDropdown";
import ModuleNavbar from "./ModuleNavbar";
import { MAX_APPS_SEED } from "../data/maxAppsDefinitions";
import { useTheme } from "../contexts/ThemeContext";
import { usePermissions } from "../hooks/usePermissions";
import { useNavigationAccess } from "../navigation/useNavigationAccess";
import {
  flattenNavForPalette,
  resolvePageTitle,
} from "../navigation/flattenNavForPalette";
import { addRecent } from "../navigation/navLauncherStorage";
import { authService, messagingService } from "../services";
import "./Dashboard.css";

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = authService.getUser();
  const { theme, toggleTheme } = useTheme();
  const { hasPermission } = usePermissions();
  const { ctx, featuresLoading } = useNavigationAccess();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const canManageAdmin = hasPermission("Admin.Manage");

  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const data = await messagingService.getUnreadCount();
        setMsgUnread(data?.count || 0);
      } catch (err) {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // 30s poll
    return () => clearInterval(interval);
  }, []);

  const paletteEntries = useMemo(() => {
    if (featuresLoading) return [];
    return flattenNavForPalette(ctx);
  }, [ctx, featuresLoading]);

  useEffect(() => {
    if (featuresLoading) return;
    const title = resolvePageTitle(location.pathname, paletteEntries);
    addRecent(location.pathname, title);

    // Dynamic Route Protection:
    // If scoping is active, check if the current path is allowed.
    // We skip checks for: 
    // - Base /home path
    // - Apps hub (/home/apps)
    // - Any paths explicitly exempt (Profile, General Settings, etc.)
    const isBaseHome = location.pathname === '/home';
    const isAppsHub = location.pathname === '/home/apps';
    
    if (ctx.navAppScopeActive && !isBaseHome && !isAppsHub && !featuresLoading) {
      // Find if the current path (or a matching prefix) exists in allowed palette entries
      const isAllowed = paletteEntries.some(e => 
        location.pathname === e.path || location.pathname.startsWith(e.path + '/')
      );
      
      // If not in allowed list, check if it's exempt or a core module entry point
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const isExempt = pathSegments.some(seg => 
         ['profile', 'notifications', 'activity-log', 'messaging', 'settings', 'reports', 'safety-observation', 'hazard-reports', 'incident', 'work-order-wizard', 'pm', 'fives', 'kaizen', 'rca', 'capa', 'opl', 'asset-master', 'work-order', 'inventory', 'breakdown-log', 'work-permits'].includes(seg)
      ) || MAX_APPS_SEED.some(app => location.pathname.startsWith(app.primaryPath));

      if (!isAllowed && !isExempt) {
        console.warn(`[NavGuard] Blocked access to unauthorized path: ${location.pathname}`);
        navigate('/home', { replace: true });
      }
    }
  }, [location.pathname, featuresLoading, paletteEntries, ctx.navAppScopeActive, navigate]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    authService.logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <header className="dashboard-shell-header">
          <div className="shell-header-top">
            <Link to="/home" className="shell-header-brand" title="Home">
              <span className="shell-header-logo-wrap">
                <MaXSidebarLogo isOpen to="/home" />
              </span>
            </Link>
            <button
              type="button"
              className="shell-search-trigger"
              onClick={() => setPaletteOpen(true)}
              aria-label="Open command palette"
            >
              <HiOutlineMagnifyingGlass aria-hidden />
              <span className="shell-search-label">Search</span>
              <kbd className="shell-kbd">⌘K</kbd>
            </button>
            <div className="shell-header-right">
              <AppLauncherDropdown />
              <SettingsLauncherDropdown />
              <NotificationDropdown />
              <div className="shell-user-menu" ref={userMenuRef}>
                <button
                  type="button"
                  className={`shell-user-chip ${userMenuOpen ? "is-open" : ""}`}
                  onClick={() => setUserMenuOpen((o) => !o)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                  aria-controls="shell-user-menu-list"
                  id="shell-user-menu-button"
                  title="Account menu"
                >
                  <span className="shell-user-avatar">
                    {authService.getTenantInfo()?.logoUrl ? (
                      <img 
                        src={authService.getTenantInfo().logoUrl} 
                        alt={authService.getTenantInfo()?.tenantName || "Logo"} 
                        className="shell-user-avatar-img"
                      />
                    ) : (
                      user?.fullName?.charAt(0)?.toUpperCase() || "U"
                    )}
                  </span>
                  <span className="shell-user-name">
                    {user?.fullName || "User"}
                  </span>
                  <HiOutlineChevronDown
                    className={`shell-user-chevron ${userMenuOpen ? "is-open" : ""}`}
                    aria-hidden
                  />
                </button>
                {userMenuOpen && (
                  <div
                    id="shell-user-menu-list"
                    className="shell-user-dropdown"
                    role="menu"
                    aria-labelledby="shell-user-menu-button"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="shell-user-dropdown-item"
                      onClick={() => {
                        navigate("/home/profile");
                        setUserMenuOpen(false);
                      }}
                    >
                      <HiOutlineUser aria-hidden />
                      Profile
                    </button>
                    {canManageAdmin && (
                      <button
                        type="button"
                        role="menuitem"
                        className="shell-user-dropdown-item shell-user-dropdown-item--admin"
                        onClick={() => {
                          navigate("/admin/dashboard");
                          setUserMenuOpen(false);
                        }}
                      >
                        <HiOutlineShieldCheck aria-hidden />
                        Admin
                      </button>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      className="shell-user-dropdown-item"
                      onClick={() => {
                        navigate("/home/notifications");
                        setUserMenuOpen(false);
                      }}
                    >
                      <HiOutlineBell aria-hidden />
                      Notifications
                    </button>
                    <button 
                      type="button" 
                      role="menuitem" 
                      className="shell-user-dropdown-item"
                      onClick={() => { navigate("/home/activity-log"); setUserMenuOpen(false); }}
                    >
                      <HiOutlineClock aria-hidden />
                      Activity Log
                    </button>
                    <button 
                      type="button" 
                      role="menuitem" 
                      className="shell-user-dropdown-item"
                      onClick={() => { navigate("/home/messaging"); setUserMenuOpen(false); }}
                    >
                      <HiOutlineChatBubbleLeftRight aria-hidden />
                      Messages
                      {msgUnread > 0 && <span className="shell-user-dropdown-badge pulse-badge">{msgUnread}</span>}
                    </button>

                    <button 
                      type="button" 
                      role="menuitem" 
                      className="shell-user-dropdown-item"
                      onClick={() => { navigate("/home/settings/general"); setUserMenuOpen(false); }}
                    >
                      <HiOutlineCog aria-hidden />
                      Settings
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="shell-user-dropdown-item"
                      onClick={() => {
                        toggleTheme();
                        setUserMenuOpen(false);
                      }}
                    >
                      {theme === "dark" ? (
                        <HiOutlineSun aria-hidden />
                      ) : (
                        <HiOutlineMoon aria-hidden />
                      )}
                      {theme === "dark" ? "Light mode" : "Dark mode"}
                    </button>
                    <div
                      className="shell-user-dropdown-divider"
                      role="separator"
                    />
                    <button
                      type="button"
                      role="menuitem"
                      className="shell-user-dropdown-item shell-user-dropdown-item--danger"
                      onClick={handleLogout}
                    >
                      <HiOutlineArrowRightOnRectangle aria-hidden />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          <ModuleNavbar />
          <Outlet />
        </div>
      </main>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        entries={paletteEntries}
        loading={featuresLoading}
      />
    </div>
  );
};

export default Dashboard;
